import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import Modal from '../components/Modal';
import './IncluirNocaute.css';

/**
 * Página para Admin registrar nocaute
 */

const IncluirNocaute = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rodadaAberta, setRodadaAberta] = useState(null);
  const [jogadoresConfirmados, setJogadoresConfirmados] = useState([]);
  const [nocauteadorId, setNocauteadorId] = useState('');
  const [nocauteadoId, setNocauteadoId] = useState('');
  const [eliminou, setEliminou] = useState(false);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [error, setError] = useState('');

  // Pop-up de rebuy logo após lançar um nocaute (sem eliminação)
  const [showRebuyModal, setShowRebuyModal] = useState(false);
  const [nocauteadoInfo, setNocauteadoInfo] = useState(null);
  const [lancandoRebuy, setLancandoRebuy] = useState(false);
  const [rebuyModalError, setRebuyModalError] = useState('');

  // Pop-up de posição final logo após lançar um nocaute com eliminação
  const [showPosicaoModal, setShowPosicaoModal] = useState(false);
  const [eliminadoInfo, setEliminadoInfo] = useState(null);
  const [posicaoInput, setPosicaoInput] = useState('');
  const [lancandoPosicao, setLancandoPosicao] = useState(false);
  const [posicaoModalError, setPosicaoModalError] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    carregarDados();
  }, [isAdmin, navigate]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setError('');

      const rodadasRes = await apiClient.get('/rodadas');
      const rodadas = Array.isArray(rodadasRes.data) ? rodadasRes.data : [];
      const aberta = rodadas.find(r => r.status === 'aberta');

      if (!aberta) {
        setError('Não há rodada aberta. Abra uma rodada primeiro.');
        setLoading(false);
        return [];
      }

      setRodadaAberta(aberta);

      // Carregar confirmações
      const confirmacoesRes = await apiClient.get(`/rodadas/${aberta.id}/confirmacoes`);
      const confirmacoes = Array.isArray(confirmacoesRes.data) ? confirmacoesRes.data : [];
      setJogadoresConfirmados(confirmacoes);
      return confirmacoes;

    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrarNocaute = async (e) => {
    e.preventDefault();
    
    if (!nocauteadorId || !nocauteadoId) {
      setError('Selecione o nocauteador e o nocauteado');
      return;
    }

    if (nocauteadorId === nocauteadoId) {
      setError('O nocauteador não pode ser o mesmo que o nocauteado');
      return;
    }

    // Guardar dados do nocauteado ANTES de resetar o formulário, para o pop-up de rebuy
    const participacaoNocauteado = jogadoresConfirmados.find(c => c.jogador_id === nocauteadoId);
    // Se o jogador já deu add-on, a eliminação é obrigatória mesmo que o checkbox
    // (travado) não tenha disparado onChange
    const foiEliminacao = eliminou || Boolean(participacaoNocauteado?.addon);

    try {
      setSalvando(true);
      setError('');

      await apiClient.post(`/rodadas/${rodadaAberta.id}/nocaute`, {
        nocauteador_id: nocauteadorId,
        nocauteado_id: nocauteadoId,
        eliminou: foiEliminacao,
      });

      setNocauteadorId('');
      setNocauteadoId('');
      setEliminou(false);

      // Recarregar a lista já refletindo o nocaute recém-lançado (ex.: jogador eliminado
      // deixa de aparecer nos selects, independente de a posição final ser lançada agora ou depois)
      const confirmacoesAtualizadas = await carregarDados();

      if (foiEliminacao) {
        // Eliminação: sugerir o lançamento da posição final na hora, já com a
        // última posição livre calculada (total de jogadores - posições já lançadas)
        const total = confirmacoesAtualizadas.length;
        const posicoesLancadas = confirmacoesAtualizadas
          .filter(c => c.posicao_final)
          .map(c => c.posicao_final);

        let posicaoSugerida = '';
        for (let i = total; i >= 1; i--) {
          if (!posicoesLancadas.includes(i)) {
            posicaoSugerida = i;
            break;
          }
        }

        setEliminadoInfo({
          jogador_id: participacaoNocauteado?.jogador_id,
          nome: participacaoNocauteado?.jogador?.nome || 'Jogador',
        });
        setPosicaoInput(posicaoSugerida === '' ? '' : String(posicaoSugerida));
        setShowPosicaoModal(true);
      } else {
        // Não foi eliminação: perguntar se quer lançar o rebuy do jogador agora
        setNocauteadoInfo({
          jogador_id: participacaoNocauteado?.jogador_id,
          nome: participacaoNocauteado?.jogador?.nome || 'Jogador',
          addon: Boolean(participacaoNocauteado?.addon),
        });
        setShowRebuyModal(true);
      }
    } catch (err) {
      console.error('Erro ao registrar nocaute:', err);
      setError(err.response?.data?.error || 'Erro ao registrar nocaute');
    } finally {
      setSalvando(false);
    }
  };

  const handleLancarRebuyRapido = async (tipo) => {
    try {
      setLancandoRebuy(true);
      setRebuyModalError('');

      await apiClient.post(`/rodadas/${rodadaAberta.id}/rebuy`, {
        jogador_id: nocauteadoInfo.jogador_id,
        tipo,
      });

      const nome = nocauteadoInfo.nome;
      setShowRebuyModal(false);
      setNocauteadoInfo(null);
      await carregarDados(); // atualizar buyin_simples/buyin_duplo/addon carregados
      alert(`Rebuy ${tipo === 'simples' ? 'simples' : 'duplo'} lançado para ${nome} com sucesso!`);
    } catch (err) {
      console.error('Erro ao lançar rebuy:', err);
      setRebuyModalError(err.response?.data?.error || 'Erro ao lançar rebuy');
    } finally {
      setLancandoRebuy(false);
    }
  };

  const handleFecharModalRebuy = () => {
    setShowRebuyModal(false);
    setNocauteadoInfo(null);
    setRebuyModalError('');
  };

  const handleLancarPosicaoFinal = async (e) => {
    e.preventDefault();

    if (!posicaoInput || posicaoInput === '') {
      setPosicaoModalError('Informe a posição final');
      return;
    }

    try {
      setLancandoPosicao(true);
      setPosicaoModalError('');

      const res = await apiClient.put(`/rodadas/${rodadaAberta.id}/posicoes-finais`, {
        participacoes: [{
          jogador_id: eliminadoInfo.jogador_id,
          posicao_final: parseInt(posicaoInput),
        }],
      });

      const nome = eliminadoInfo.nome;
      const campeao = res.data?.sucesso?.find(s => s.automatico);

      setShowPosicaoModal(false);
      setEliminadoInfo(null);
      setPosicaoInput('');
      await carregarDados();

      if (campeao) {
        alert(
          `Posição ${posicaoInput} lançada para ${nome}!\n\n` +
          `🏆 Como só restava ${campeao.jogador_nome}, ele foi automaticamente marcado como campeão (1º lugar)!`
        );
      } else {
        alert(`Posição ${posicaoInput} lançada para ${nome} com sucesso!`);
      }
    } catch (err) {
      console.error('Erro ao lançar posição final:', err);
      setPosicaoModalError(err.response?.data?.error || 'Erro ao lançar posição final');
    } finally {
      setLancandoPosicao(false);
    }
  };

  const handleFecharModalPosicao = () => {
    setShowPosicaoModal(false);
    setEliminadoInfo(null);
    setPosicaoInput('');
    setPosicaoModalError('');
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  if (!rodadaAberta) {
    return (
      <div className="container">
        <div className="error">Não há rodada aberta. Abra uma rodada primeiro.</div>
        <button onClick={() => navigate('/rodadas')} className="btn btn-primary">
          Ir para Rodadas
        </button>
      </div>
    );
  }

  // Jogador que já deu add-on não tem mais rebuy possível: qualquer nocaute nele
  // é sempre eliminação, então a flag vem marcada e travada para evitar esquecimento.
  const nocauteadoSelecionado = jogadoresConfirmados.find(c => c.jogador_id === nocauteadoId);
  const eliminacaoObrigatoria = Boolean(nocauteadoSelecionado?.addon);

  return (
    <div className="container incluir-nocaute-container">
      <div className="page-header">
        <h1>🥊 Registrar Nocaute</h1>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-secondary">
          ← Voltar
        </button>
      </div>

      <div className="rodada-info">
        <p><strong>Rodada:</strong> {new Date(rodadaAberta.data).toLocaleDateString('pt-BR')}</p>
      </div>

      {error && <div className="error">{error}</div>}

      <form onSubmit={handleRegistrarNocaute} className="nocaute-form">
        <div className="form-group">
          <label>Nocauteador (quem deu o nocaute) *</label>
          <select
            value={nocauteadorId}
            onChange={(e) => setNocauteadorId(e.target.value)}
            required
          >
            <option value="">Selecione o nocauteador...</option>
            {jogadoresConfirmados
              .filter(c => !c.eliminado)
              .map(c => (
                <option key={c.jogador_id} value={c.jogador_id}>
                  {c.jogador?.nome || 'Jogador'}
                </option>
              ))}
          </select>
        </div>

        <div className="form-group">
          <label>Nocauteado (quem recebeu o nocaute) *</label>
          <select
            value={nocauteadoId}
            onChange={(e) => setNocauteadoId(e.target.value)}
            required
          >
            <option value="">Selecione o nocauteado...</option>
            {jogadoresConfirmados
              .filter(c => c.jogador_id !== nocauteadorId && !c.eliminado)
              .map(c => (
                <option key={c.jogador_id} value={c.jogador_id}>
                  {c.jogador?.nome || 'Jogador'}
                </option>
              ))}
          </select>
        </div>

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: eliminacaoObrigatoria ? 'default' : 'pointer' }}>
            <input
              type="checkbox"
              checked={eliminacaoObrigatoria ? true : eliminou}
              disabled={eliminacaoObrigatoria}
              onChange={(e) => setEliminou(e.target.checked)}
              style={{ width: '20px', height: '20px', cursor: eliminacaoObrigatoria ? 'default' : 'pointer' }}
            />
            <span>💀 Eliminou (jogador foi eliminado da rodada)</span>
          </label>
          {eliminacaoObrigatoria ? (
            <small style={{ display: 'block', marginTop: '5px', color: '#856404' }}>
              ⚠️ Esse jogador já deu add-on e não tem mais rebuy possível — qualquer nocaute nele é eliminação.
            </small>
          ) : (
            <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
              Se marcado, a mensagem no WhatsApp será diferente indicando eliminação
            </small>
          )}
        </div>

        <div className="form-actions">
          <button type="submit" disabled={salvando} className="btn btn-primary">
            {salvando ? 'Registrando...' : 'Registrar Nocaute'}
          </button>
        </div>
      </form>

      <Modal
        isOpen={showRebuyModal}
        onClose={handleFecharModalRebuy}
        title="Lançar rebuy?"
      >
        <p>
          <strong>{nocauteadoInfo?.nome}</strong> foi nocauteado. Deseja lançar o rebuy dele agora?
        </p>

        {rebuyModalError && <div className="error">{rebuyModalError}</div>}

        {nocauteadoInfo?.addon ? (
          <div className="aviso-rebuy-addon">
            ⚠️ Esse jogador já deu add-on nesta rodada — não é mais possível lançar rebuy.
            Confira se esse nocaute deveria estar marcado como eliminação.
          </div>
        ) : (
          <div className="rebuy-modal-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={lancandoRebuy}
              onClick={() => handleLancarRebuyRapido('simples')}
            >
              {lancandoRebuy ? 'Lançando...' : 'Rebuy Simples (R$ 25,00)'}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={lancandoRebuy}
              onClick={() => handleLancarRebuyRapido('duplo')}
            >
              {lancandoRebuy ? 'Lançando...' : 'Rebuy Duplo (R$ 40,00)'}
            </button>
          </div>
        )}

        <div className="rebuy-modal-footer">
          <button type="button" className="btn btn-secondary" onClick={handleFecharModalRebuy}>
            {nocauteadoInfo?.addon ? 'Fechar' : 'Pular'}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={showPosicaoModal}
        onClose={handleFecharModalPosicao}
        title="Lançar posição final"
      >
        <p>
          <strong>{eliminadoInfo?.nome}</strong> foi eliminado. Lance a posição final dele agora.
        </p>

        {posicaoModalError && <div className="error">{posicaoModalError}</div>}

        <form onSubmit={handleLancarPosicaoFinal}>
          <div className="form-group">
            <label>Posição Final *</label>
            <input
              type="number"
              min="1"
              value={posicaoInput}
              onChange={(e) => setPosicaoInput(e.target.value)}
              className="posicao-input"
              autoFocus
              required
            />
          </div>

          <div className="rebuy-modal-actions">
            <button type="submit" disabled={lancandoPosicao} className="btn btn-primary">
              {lancandoPosicao ? 'Lançando...' : 'Lançar Posição'}
            </button>
          </div>
        </form>

        <div className="rebuy-modal-footer">
          <button type="button" className="btn btn-secondary" onClick={handleFecharModalPosicao}>
            Lançar depois
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default IncluirNocaute;
